/**
 * Classes partagées par tous les contrôles de saisie.
 *
 * Parti pris : un champ REMPLI, pas un champ bordé.
 *
 * Le contour marqué (bordure moyenne + ombre portée + ombre interne) est le
 * réflexe des formulaires des années 2010 : il empile trois indices pour dire
 * une seule chose, et alourdit visuellement toute page qui contient plus de
 * deux champs. Ici c'est le FOND qui identifie la zone de saisie, et la bordure
 * n'est qu'un liseré discret.
 *
 * Le champ « s'éclaire » au focus : il remonte de `surface-sunken` à
 * `surface-raised`. Le changement de fond est bien plus lisible qu'un simple
 * changement de couleur de bordure, et il indique où l'on écrit sans crier.
 *
 * ── Accessibilité, en toute transparence ────────────────────────────────────
 * WCAG 1.4.11 demande 3:1 pour l'indice visuel qui identifie un composant.
 * Un liseré doux ne l'atteint pas — et un fond doux non plus : atteindre 3:1
 * entre le champ et la page imposerait un gris soutenu sur toute la surface,
 * ce qu'aucun système sobre ne fait.
 *
 * D'où le choix explicite : apparence douce par défaut, contour renforcé dès
 * que l'utilisateur demande `prefers-contrast: more` (variante `contrast-more:`).
 * C'est un arbitrage assumé et réversible, pas un oubli.
 */
export const controlBase = [
  "w-full rounded-lg text-fg text-base sm:text-sm touch-manipulation",

  // Le fond porte l'identification du champ ; la bordure n'est qu'un liseré.
  "border border-border/80 bg-surface-sunken",
  "placeholder:text-fg-muted",

  // `box-shadow` est hors de la transition : l'anneau de focus est une ombre,
  // et l'interpoler la ferait repeindre à chaque image pendant qu'elle grandit.
  // Un anneau de focus qui apparaît d'un coup est de toute façon la convention
  // des contrôles natifs — c'est un accusé de réception, pas une animation.
  "transition-[background-color,border-color] duration-fast ease-standard",

  // Le survol densifie le liseré : le champ signale qu'il est atteignable.
  "hover:border-border-strong",

  // Au focus, le champ remonte d'un cran et son liseré s'épaissit — sans halo.
  //
  // L'anneau translucide de 2 à 3px (`ring-accent/25`) est l'écueil classique :
  // à cette opacité il ne délimite plus rien, il diffuse. On lit une auréole
  // floue collée au champ, et sur un formulaire dense chaque champ visité en
  // laisse une. Ici l'anneau est OPAQUE et fait 1px, posé au ras de la bordure :
  // les deux se lisent comme un unique liseré accent de 2px, net, qui suit
  // exactement l'arrondi. C'est le même signal, sans le flou.
  "focus:border-accent focus:bg-surface-raised focus:ring-1 focus:ring-accent focus:outline-none shadow-xs",

  // Contour renforcé pour qui demande davantage de contraste.
  "contrast-more:border-border-strong",

  "disabled:cursor-not-allowed disabled:border-transparent disabled:text-fg-subtle",

  // Sélecteurs natifs de date et d'heure : l'icône du navigateur est opaque et
  // dépareillée. On l'atténue au repos et on la révèle au survol.
  "[&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-45",
  "hover:[&::-webkit-calendar-picker-indicator]:opacity-100",
  // Les flèches natives des champs numériques sont minuscules et laides :
  // `NumberInput` fournit ses propres boutons à la place.
  "[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
  "[&[type=number]]:[-moz-appearance:textfield]",

  // Piloté par aria-invalid : l'état visuel et l'état accessible ne peuvent pas diverger.
  "aria-invalid:border-danger aria-invalid:bg-danger-subtle/40 aria-invalid:focus:border-danger aria-invalid:focus:ring-danger",

  // `:user-invalid` et non `:invalid` : le premier ne s'active qu'APRÈS que la
  // personne a réellement interagi avec le champ. `:invalid` seul allume en
  // rouge un formulaire vierge dont on n'a encore rien rempli — c'est le
  // reproche classique fait à la validation native, et la raison pour laquelle
  // tant d'équipes la réimplémentent en JavaScript. Ici la contrainte HTML
  // (`required`, `type="email"`, `pattern`) suffit, sans une ligne de script.
  "user-invalid:border-danger user-invalid:bg-danger-subtle/40",
].join(" ");

/** Hauteurs et paddings généreux alignés sur le confort tactile smartphone (48px min sur mobile). */
export const controlSizes = {
  sm: "h-10 sm:h-8 px-3 sm:px-2.5 text-base sm:text-xs font-normal rounded-md",
  md: "h-12 sm:h-9 px-4 sm:px-3 text-base sm:text-sm font-normal rounded-lg",
  lg: "h-14 sm:h-11 px-4 sm:px-3.5 text-base font-normal rounded-xl",
} as const;

export type ControlSize = keyof typeof controlSizes;

/**
 * État « déployé » d'un contrôle qui commande un popover (select, date, heure,
 * couleur). Il reprend exactement le liseré du focus : le champ ouvert et le
 * champ focalisé sont le même état pour l'utilisateur, ils doivent donc se
 * ressembler au pixel près.
 */
export const controlOpen = "border-accent ring-1 ring-accent";

/**
 * Réservation de place pour les ornements (loupe, « € », bouton d'effacement).
 *
 * Le retrait dépend de la taille : à `md`, le champ a déjà 16px de padding sur
 * mobile et 12px sur desktop. Un `pl-9` unique — hérité du temps où toutes les
 * tailles partageaient le même padding — collait la loupe au texte au point
 * qu'on ne distinguait plus l'icône du contenu saisi.
 *
 * Le calcul est explicite : retrait du champ + 16px d'icône + 8px de respiration.
 */
export const controlOrnament = {
  sm: {
    leadingInset: "left-3 sm:left-2.5",
    trailingInset: "right-3 sm:right-2.5",
    leading: "pl-9 sm:pl-8",
    trailing: "pr-9 sm:pr-8",
  },
  md: {
    leadingInset: "left-4 sm:left-3",
    trailingInset: "right-4 sm:right-3",
    leading: "pl-10 sm:pl-9",
    trailing: "pr-10 sm:pr-9",
  },
  lg: {
    leadingInset: "left-4 sm:left-3.5",
    trailingInset: "right-4 sm:right-3.5",
    leading: "pl-11 sm:pl-10",
    trailing: "pr-11 sm:pr-10",
  },
} as const;

/**
 * Variante pour les contrôles qui ne sont pas un `<input>` mais doivent lui
 * ressembler : zone de dépôt de fichier, champ à jetons, groupe de cases OTP.
 * Le focus y est porté par `focus-within` puisque l'élément focalisé est un
 * descendant.
 */
export const controlShellBase = [
  "w-full rounded-lg",
  "border border-border/80 bg-surface-sunken",
  // `box-shadow` est hors de la transition : l'anneau de focus est une ombre,
  // et l'interpoler la ferait repeindre à chaque image pendant qu'elle grandit.
  // Un anneau de focus qui apparaît d'un coup est de toute façon la convention
  // des contrôles natifs — c'est un accusé de réception, pas une animation.
  "transition-[background-color,border-color] duration-fast ease-standard",
  "hover:border-border-strong",
  "focus-within:border-accent focus-within:bg-surface-raised focus-within:ring-1 focus-within:ring-accent focus-within:outline-none shadow-xs",
  "contrast-more:border-border-strong",
  "aria-invalid:border-danger",
].join(" ");
